import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "sonner";
import { Loader2 } from "lucide-react";
import { CookieConsent } from "@/components/CookieConsent";
import { PageTracker } from "@/components/PageTracker";
import { ChatAssistant } from "@/components/ChatAssistant";
import { TutorialProvider } from "@/context/TutorialContext";
import { TutorialOverlay } from "@/components/TutorialOverlay";

// ── Critical routes (loaded eagerly — users land here immediately) ─────────
import Landing from "@/pages/Landing";
import AuthPage from "@/pages/AuthPage";
import CampaignShare from "@/pages/CampaignShare";
import { ForgotPassword, ResetPassword } from "@/pages/PasswordReset";

// ── Marketing pages (lazy — lightweight but not on the hot path) ──────────
const Pricing      = lazy(() => import("@/pages/Pricing"));
const Contact      = lazy(() => import("@/pages/Contact"));
const Blog         = lazy(() => import("@/pages/Blog"));
const UseCasesIndex = lazy(() => import("@/pages/UseCasesIndex"));
const UseCase      = lazy(() => import("@/pages/UseCase"));
const About        = lazy(() => import("@/pages/About"));
const PrivacyPolicy = lazy(() => import("@/pages/PrivacyPolicy"));
const Terms        = lazy(() => import("@/pages/Terms"));

// ── App pages (lazy — auth-gated; biggest bundle impact) ──────────────────
const Dashboard  = lazy(() => import("@/pages/Dashboard")); // Studio + dashboard tabs
const Gallery    = lazy(() => import("@/pages/Gallery"));
const Editor     = lazy(() => import("@/pages/Editor"));    // contains react-filerobot
const BrandKits  = lazy(() => import("@/pages/BrandKits"));
const Campaigns  = lazy(() => import("@/pages/Campaigns"));
const Admin      = lazy(() => import("@/pages/Admin"));

// ── Loading fallback ────────────────────────────────────────────────────────
function PageLoader() {
  return (
    <div className="h-screen w-full flex items-center justify-center bg-[#050508]">
      <Loader2 className="h-7 w-7 text-primary animate-spin" />
    </div>
  );
}

// ── Auth guards ────────────────────────────────────────────────────────────
function RequireAuth({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <PageLoader />;
  if (!user) return <Navigate to={`/login?next=${encodeURIComponent(location.pathname)}`} replace />;
  return children;
}

function RequireAdmin({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <PageLoader />;
  if (!user || user?.role !== "admin") return <Navigate to="/studio" replace />;
  return children;
}

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <BrowserRouter>
          <TutorialProvider>
          <PageTracker />
          <Toaster
            position="top-right"
            toastOptions={{
              style: { background: "#0F1015", border: "1px solid rgba(255,255,255,0.1)", color: "#fff" },
            }}
          />
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* Public marketing */}
              <Route path="/" element={<Landing />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/use-cases" element={<UseCasesIndex />} />
              <Route path="/use-cases/:slug" element={<UseCase />} />
              <Route path="/about" element={<About />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<Terms />} />

              {/* Auth */}
              <Route path="/login" element={<AuthPage mode="login" />} />
              <Route path="/register" element={<AuthPage mode="register" />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/c/:token" element={<CampaignShare />} />

              {/* App (auth-gated) */}
              <Route path="/studio" element={<RequireAuth><Dashboard /></RequireAuth>} />
              <Route path="/gallery" element={<RequireAuth><Gallery /></RequireAuth>} />
              <Route path="/editor" element={<RequireAuth><Editor /></RequireAuth>} />
              <Route path="/brand" element={<RequireAuth><BrandKits /></RequireAuth>} />
              <Route path="/campaigns" element={<RequireAuth><Campaigns /></RequireAuth>} />
              <Route path="/admin" element={<RequireAdmin><Admin /></RequireAdmin>} />

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
          <ChatAssistant />
          <CookieConsent />
          <TutorialOverlay />
          </TutorialProvider>
        </BrowserRouter>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
